import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Paperclip, Trash2 } from 'lucide-react';
import { getRevenue, updateRevenue, getRevenueCategories } from '../../../../services/finance_services/revenueService';
import { getRevenueAttachments, uploadRevenueAttachment, deleteRevenueAttachment } from '../../../../services/finance_attachment_services/revenueAttachmentService';
import ReportForm from '../../../../components/ReportForm';
import Message from '../../../../components/Message';
import useApi from '../../../../hooks/useApi';

const EditInviterRevenuePage = () => {
  const navigate = useNavigate();
  const { invitationId, id } = useParams();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [dateReceived, setDateReceived] = useState('');
  const [category, setCategory] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [client, setClient] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [categories, setCategories] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [deletingAttachments, setDeletingAttachments] = useState([]);

  // Use useApi hooks
  const { loading: updateLoading, error, successMessage, callApi: updateRevenueApi, setError, setSuccessMessage } = useApi((id, data) => updateRevenue(id, data, invitationId), {showSuccessToast: false, msg:"Revenue updated successfully"});
  const { loading: fetchLoading, error: fetchError, callApi: fetchRevenueData } = useApi((id) => getRevenue(id, invitationId), {showSuccessToast: false});
  const { callApi: fetchCategories } = useApi(() => getRevenueCategories(invitationId), {showSuccessToast: false});

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch revenue data using useApi
        const revenueData = await fetchRevenueData(id);
        if (revenueData) {
          setTitle(revenueData.title || '');
          setDescription(revenueData.description || '');
          setAmount(revenueData.amount !== undefined && revenueData.amount !== null ? String(revenueData.amount) : '');
          setDateReceived(revenueData.date_received ? new Date(revenueData.date_received).toISOString().slice(0, 10) : '');
          setCategory(revenueData.category ? String(revenueData.category) : '');
          setPaymentMethod(revenueData.payment_method || '');
          setClient(revenueData.client || '');
          setReferenceNumber(revenueData.reference_number || '');
        }

        // Fetch categories using useApi
        const catData = await fetchCategories();
        if (catData) {
          setCategories(catData);
        }

        // Fetch attachments
        const attachments = await getRevenueAttachments(id, invitationId);
        setExistingAttachments(attachments?.data || []);
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };
    
    fetchData();
  }, [id, invitationId]); // Removed fetchRevenueData and fetchCategories from dependencies

  const handleFileChange = (e) => {
    setFiles([...e.target.files]);
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      setDeletingAttachments(prev => [...prev, attachmentId]);
      await deleteRevenueAttachment(id, attachmentId, invitationId);
      setExistingAttachments(prev => 
        prev.filter(attachment => attachment.id !== attachmentId)
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingAttachments(prev => 
        prev.filter(id => id !== attachmentId)
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const revenueData = {
      title,
      description,
      amount,
      date_received: dateReceived,
      category,
      payment_method: paymentMethod,
      client,
      reference_number: referenceNumber,
    };
    
    try {
      // Update the revenue using useApi
      await updateRevenueApi(id, revenueData);
      
      // Handle new attachments if any
      if (files.length > 0) {
        setUploadingAttachments(true);
        await Promise.all(
          files.map(file => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('title', file.name);
            return uploadRevenueAttachment(id, formData, invitationId);
          })
        );
        setUploadingAttachments(false);
        
        // Refresh attachments after upload
        const attachments = await getRevenueAttachments(id, invitationId);
        setExistingAttachments(attachments?.data || []);
      }
      
      setSuccessMessage('Revenue updated successfully!');
      setTimeout(() => {
        navigate(`/dashboard/collaborator/${invitationId}/revenues`);
      }, 1500);
    } catch (err) {
      setError(err.message);
    }
  };

  const formFields = [
    { name: 'title', label: 'Title', type: 'text', value: title, onChange: (e) => setTitle(e.target.value), required: true },
    { name: 'description', label: 'Description', type: 'textarea', value: description, onChange: (e) => setDescription(e.target.value) },
    { name: 'amount', label: 'Amount', type: 'number', value: amount, onChange: (e) => setAmount(e.target.value), required: true },
    { name: 'date_received', label: 'Date Received', type: 'date', value: dateReceived, onChange: (e) => setDateReceived(e.target.value), required: true },
    { name: 'category', label: 'Category', type: 'select', value: category, onChange: (e) => setCategory(e.target.value), options: categories || [], required: true },
    { name: 'payment_method', label: 'Payment Method', type: 'text', value: paymentMethod, onChange: (e) => setPaymentMethod(e.target.value) },
    { name: 'client', label: 'Client', type: 'text', value: client, onChange: (e) => setClient(e.target.value) },
    { name: 'reference_number', label: 'Reference Number', type: 'text', value: referenceNumber, onChange: (e) => setReferenceNumber(e.target.value) },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(`/dashboard/collaborator/${invitationId}/revenues`)}
          className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Revenues
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Edit Revenue</h1>
      </div>

      {/* Display API Error Message */}
      <Message type="error" message={error || fetchError} />

      {/* Display Success Message */}
      <Message type="success" message={successMessage} />

      {/* Revenue Form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <ReportForm
        title="Edit Revenue"
        fields={formFields}
        onSubmit={handleSubmit}
        onCancel={() => navigate(`/dashboard/collaborator/${invitationId}/revenues`)}
        loading={updateLoading || uploadingAttachments}
        submitButtonText="Update Revenue"
      >
        {/* Existing Attachments */}
        {existingAttachments.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700">Current Attachments</label>
            <ul className="mt-2 space-y-2">
              {existingAttachments.map(attachment => (
                <li key={attachment.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                  <div className="flex items-center">
                    <Paperclip className="h-4 w-4 mr-2 text-gray-500" />
                    <span>{attachment.title || attachment.file_name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteAttachment(attachment.id)}
                    className="text-red-600 hover:text-red-800"
                    disabled={deletingAttachments.includes(attachment.id)}
                  >
                    {deletingAttachments.includes(attachment.id) ? (
                      'Deleting...'
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* New Attachments */}
        <div>
          <label htmlFor="attachments" className="block text-sm font-medium text-gray-700">
            Add New Attachments (Receipts, Invoices, etc.)
          </label>
          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
            <div className="space-y-1 text-center">
              <Paperclip className="mx-auto h-12 w-12 text-gray-400" />
              <div className="flex text-sm text-gray-600">
                <label
                  htmlFor="file-upload"
                  className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none"
                >
                  <span>Upload files</span>
                  <input
                    id="file-upload"
                    name="file-upload"
                    type="file"
                    className="sr-only"
                    multiple
                    onChange={handleFileChange}
                  />
                </label>
                <p className="pl-1">or drag and drop</p>
              </div>
              <p className="text-xs text-gray-500">
                PDF, JPG, PNG up to 10MB
              </p>
            </div>
          </div>
          {files.length > 0 && (
            <div className="mt-2">
              <p className="text-sm text-gray-500">Files to upload:</p>
              <ul className="mt-1 text-sm text-gray-700">
                {Array.from(files).map((file, index) => (
                  <li key={index} className="flex items-center">
                    <Paperclip className="h-4 w-4 mr-2 text-gray-500" />
                    {file.name} ({Math.round(file.size / 1024)} KB)
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </ReportForm>
      </div>
    </div>
  );
};

export default EditInviterRevenuePage;